/**
 * 生成設定の永続データモデル（v7）。
 *
 * `Office.context.document.settings` の単一キー `im.prompts` に {@link SettingsPayload} として
 * 格納する。キー名は v6 から変えない（バックエンドの抽出がプロパティ名で正本を選ぶため、および
 * 社外向け出力の settings クリアが `im.*` を単位に扱うため）。
 *
 * v6 からの変更:
 * - フロー（`conditions` / `edges` / `positions`）を廃止した。実測で条件ノード 0 件、
 *   エッジ 17 本はすべて順序依存のみ・データ依存 0 のため。
 * - `prompts` を {@link Assignment}（生成担当）へ改め、`labels` を廃止した。
 *   枠の名前は `PowerPoint.Shape.name` が正本になり settings は持たない。
 * - スライドと要素の設定（{@link SlideSetting} / {@link ElementSetting}）を新設した。
 *
 * 設計: [[2026-08-26 設計ノート アドイン 生成設定UIの再構成]]。
 */

/**
 * 対象の参照。`slide:<スライドの識別子>` または `element:<要素の識別子>`。
 * 識別子はアドインが発行し、`Slide.tags` / `Shape.tags` が正本（v8）。
 */
export type TargetRef = string;

/**
 * 裁量の 3 位置。未設定は `embed`（埋め込み）として扱う。
 *
 * 以前は「未設定＝枠の種別から導出する」を表す `auto` を持っていたが、導出は実装されないまま
 * 選択肢だけが残っていた。既定を `embed` に定めて `auto` は廃止する。保存済みの `auto` は
 * 読み込んだ時点で `embed` へ読み替える（`SettingsGenerationSettingStore`）。
 */
export type Discretion = "embed" | "compose" | "propose";

/** 入りきらないときの梯子。段が上がるほど動かす構造が大きい。 */
export type Overflow = "fit" | "other" | "grow" | "nextSlide";

/** 余った行の扱い。スライドの削除条件と語をそろえる。 */
export type LeftoverRows = "keep" | "delete";

/** 何枚出すか。件数の判断は AI、紙面の展開はプログラムが行う。 */
export type Multiplicity = {
    /**
     * 数え方（散文・任意）。資料から何を数えるか。
     * 空でも上限だけで意味を持つ（上限まで出しうるが、何を数えるかは AI に委ねる）。
     */
    counting?: string;
    /** 上限枚数。展開はここで止まる。 */
    max: number;
};

/**
 * スライド単位の設定。フォーマットに属し、案件を跨いで不変。
 * 名前は `PowerPoint.Slide` に持ち場が無いためここが正本。
 */
export type SlideSetting = {
    /** スライドの名前。文書の目次にあたる。 */
    name?: string;
    /** ねらい。何を伝える場か。 */
    aim?: string;
    /** 何枚出すか。未設定なら常に 1 枚。 */
    multiplicity?: Multiplicity;
    /** 削除条件。どういうときにこの枚を消すか（散文・任意）。未設定なら消さない。 */
    deleteWhen?: string;
    updatedAt: string;
    updatedBy: string;
};

/**
 * 要素単位の設定。フォーマットに属し、案件を跨いで不変。
 *
 * 名前はここが正本。`PowerPoint.Shape.name` は書き換えない。実データの図形名は
 * `Rectangle 28` のような自動採番が大半で、1,170 図形のうち人が付けた一意の名前は
 * 事実上ゼロだった。初回の走査でそこから取り込み、以降はここで育てる。
 * 設計: docs/notes/設計ノート/2026-08-31 設計ノート アドイン 生成設定の識別子.md
 */
export type ElementSetting = {
    /** 枠の名前。初回は図形名を取り込む。以降は利用者が直したものが残る。 */
    name?: string;
    /** ねらい。この枠が何を担うか。 */
    aim?: string;
    /** 作成ルール。書式と表現の要件（任意）。 */
    rule?: string;
    /** 裁量。契約の選択子。未設定は `embed`（埋め込み）として扱う。 */
    discretion?: Discretion;
    /** 入りきらないときの扱い。表の枠のみ。未設定は `fit` として扱う。 */
    overflow?: Overflow;
    /** 余った行の扱い。表の枠のみ。未設定は `keep` として扱う。 */
    leftoverRows?: LeftoverRows;
    /**
     * `grow`（行を増やす）で足してよい行数の閾値。表の枠のみ。0 以上の整数。
     * 足す行数がこれを超えたら、行を増やさず次の枚へ送る（組立が行う）。
     * 未設定は「自動」で、組立が表の下端と下の付随要素の位置から求める。
     * 設計: docs/notes/設計ノート/2026-10-01設計ノート表のあふれをgrowから次の枚へ送る.md
     */
    maxRowsBeforeNext?: number;
    updatedAt: string;
    updatedBy: string;
};

/**
 * 生成が増やした紙面。**フォーマット時点には無かったもの**で、組立が編集用 PPTX へ書き戻す。
 *
 * 反復の複製と提案の案は、生成のときに初めて生まれる枚である。設定はフォーマットのものだけを
 * 持つため、増えた枚はどの設定にも紐づかず、成果物にだけ存在していた。それを宣言した担当が
 * 覚えることで、「どれを複製・提案したか」を次に開いたときに言える。
 *
 * `elements` が元の枠から生まれた枠への対応を持つので、担当の `targets` をたどれば複製側の枠を
 * 指せる。増えた紙面だけを画面へ出す、案を選ぶ、といった操作の土台になる。
 *
 * **書き手はサーバーだけである。** アドインは読むだけで、編集時も素通しする。
 */
export type GeneratedSlide = {
    /** 何によって生まれたか。`repeat` = 反復の複製 / `proposal` = 提案の案。 */
    kind: "repeat" | "proposal";
    /** 元になった枚。設定を持っているのはこちら。 */
    from: TargetRef;
    /** 生まれた枚。 */
    slide: TargetRef;
    /**
     * 反復の項目番号、または案の番号。ふつうは 2 以上（1 番は元の枚に入る）。
     * 拠点ごとに並べる担当では一番早い拠点の項目が元の枚に入るので、1 番が複製に入ることもある。
     */
    number: number;
    /** 元の枠 → 生まれた枠 の対応。 */
    elements: { [from: string]: TargetRef };
    /** どの生成タスクが作ったか。再生成を重ねたとき前回の産物を見分けられる。 */
    taskId?: string;
};

/**
 * 生成担当。1 件が 1 つの実行 AI へ渡る仕事の単位。
 *
 * `skipTargets` と `skip` は実行ごとの旗で、フォーマットの既定としてここに持つ。
 * タスクごとの上書きは既存のオーバーレイ機構が担う
 * （[[2026-08-03 設計ノート 生成タスクpromptsのskipオーバーレイ化]]）。
 */
export type Assignment = {
    /** ラベル。空文字は下書きを意味し、ストアには書かれない。 */
    label: string;
    /** 指示の本文。メンショントークン `@[表示名](ref)` を含む原文。 */
    instruction: string;
    /** 本文のメンションから導出した対象。書き込みのたびに再計算して保存する。 */
    targets: TargetRef[];
    /**
     * 今回の実行で書き込まない対象。`targets` の部分集合。
     * 担当は実行されるが、ここに挙がった対象へは書き込まない（再生成の粒度）。
     * `targets` をすべて覆うと、その担当は呼ばれない。
     */
    skipTargets?: TargetRef[];
    /** 今回の実行でこの担当を呼ばない。対象ごとの設定に関わらず効く。 */
    skip?: boolean;
    /**
     * この担当の生成が増やした紙面。**サーバーが書き、アドインは読むだけ。**
     * 実行のたびに置き換わるので、前回の産物を指したままにはならない。
     */
    generated?: GeneratedSlide[];
    updatedAt: string;
    updatedBy: string;
};

/** slideId をキーとする {@link SlideSetting} のマップ。 */
export type SlideSettingMap = { [slideUid: string]: SlideSetting };

/** 要素の識別子をキーとする {@link ElementSetting} のマップ。 */
export type ElementSettingMap = { [elementUid: string]: ElementSetting };

/** assignmentId をキーとする {@link Assignment} のマップ。 */
export type AssignmentMap = { [assignmentId: string]: Assignment };

/** settings キー `im.prompts` に格納するペイロード（v7）。 */
export type SettingsPayload = {
    version: number;
    slides: SlideSettingMap;
    elements: ElementSettingMap;
    assignments: AssignmentMap;
    /** 内部用 PPTX に埋め込まれた生成元タスク ID。根拠 API 呼び出しの手がかり。 */
    sourceTaskId?: string;
    /** 内部用 PPTX に埋め込まれた生成元プロジェクト ID。 */
    sourceProjectId?: string;
};

/** 現行ペイロードバージョン。v6=フロー機構つき / v7=フロー廃止・スライドと要素の設定を新設。 */
export const SETTINGS_PAYLOAD_VERSION = 8;
